import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Section: Menu drawer
 * -----------------------------------------------------------------------------
 * Scripts to initiate menu drawer toggle and also display on admin.
 *
 * @namespace menuDrawer
 */

import { register } from '@shopify/theme-sections';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import cssClasses from '~/helpers/cssClasses';
import { on, getSiblings } from '~/helpers/utils';
import Toggle from '~/components/toggle';
/**
 * DOM selectors.
 */

const selectors = {
  body: '[js-menu-drawer="body"]',
  tier2MenuToggle: '[js-menu-drawer="tier2MenuToggle"]',
  container: '[js-menu-drawer="container"]',
  tier3MenuToggle: '[js-menu-drawer="tier3MenuToggle"]'
};
/**
 * Instantiate menu drawer toggle.
 */

const MenuDrawer = Toggle({
  toggleSelector: 'menuDrawer',
  overlay: false
});
/**
 * Component selectors.
 */

const menus = [...document.querySelectorAll(selectors.tier2MenuToggle), ...document.querySelectorAll(selectors.tier3MenuToggle)];
/**
 * Menu drawer section constructor.
 * Executes on page load as well as Theme Editor `section:load` events.
 */

register('menu-drawer', {
  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.body = document.querySelector(selectors.body);
    this.container = document.querySelector(selectors.container);
  },

  /**
   * Initiate toggle events on all sub-menus.
   */
  setEventListeners() {
    [...menus].forEach(element => {
      on('click', element, event => this.onItemClick(event, element));
    });
  },

  /**
   * Handle toggle event on menu item click.
   * @param {Event} event - Click event.
   * @param {HTMLElement} element - The menu item.
   */
  onItemClick(event, element) {
    event.preventDefault();
    this.toggleElement(element);
    this.toggleElement(getSiblings(element)[0]);
  },

  /**
   * Set EventBus listeners for each instance.
   */
  setEventBusListeners() {
    Frame.EventBus.listen(['Toggle:menuDrawer:open'], () => disableBodyScroll(this.body));
    Frame.EventBus.listen(['Toggle:menuDrawer:close'], () => enableBodyScroll(this.body));
  },

  /**
   * Check if item is active.
   * @param {HTMLElement} element - The menu item.
   * @returns {Boolean}
   */
  isItemActive(element) {
    return element.classList.contains(cssClasses.active);
  },

  /**
   * Toggle button state.
   * @param {HTMLElement} element - The toggled button.
   */
  toggleElement(element) {
    if (this.isItemActive(element)) {
      this.showMenu(element);
      return;
    }

    this.hideMenu(element);
  },

  /**
   * Show menu.
   * @param {HTMLElement} element - The item to show.
   */
  showMenu(element) {
    element.classList.remove(cssClasses.active);
  },

  /**
   * Hide menu.
   * @param {HTMLElement} element - The item to hide.
   */
  hideMenu(element) {
    element.classList.add(cssClasses.active);
  },

  /**
   * Initialise section.
   */
  init() {
    MenuDrawer.init();
    this.setNodeSelectors();
    this.setEventListeners();
  },

  /**
   * Event callback for Theme Editor `section:select` event
   */
  onSelect() {
    MenuDrawer.open();
  },

  /**
   * Event callback for Theme Editor `shopify:section:deselect` event.
   */
  onDeselect() {
    MenuDrawer.close();
  },

  /**
   * Event callback for Theme Editor `section:load` event
   */
  onLoad() {
    this.init();
    this.setEventBusListeners();
  }

});